import React from 'react';
import { getCoffeeIconUrl } from '../hooks/useCoffeeShops';

interface CoffeeBadgeProps {
    coffeeKey: string;
    displayName: string;
    iconName?: string;
    rating?: number;
    count?: number;
    size?: 'sm' | 'md';
    onClick?: () => void;
    selected?: boolean;
}

const CoffeeBadge: React.FC<CoffeeBadgeProps> = ({
    coffeeKey,
    displayName,
    iconName,
    rating,
    count,
    size = 'md',
    onClick,
    selected = false
}) => {
    const iconUrl = getCoffeeIconUrl(iconName || coffeeKey);

    const sizeStyles = {
        sm: {
            container: 'px-2.5 py-1.5 gap-2',
            icon: 'w-5 h-5',
            name: 'text-sm font-bold',
            stats: 'text-xs font-black'
        },
        md: {
            container: 'px-3 py-2 gap-2',
            icon: 'w-6 h-6',
            name: 'text-sm font-bold',
            stats: 'text-xs font-black'
        }
    };

    const styles = sizeStyles[size];

    // color the rating based on score
    const getRatingColor = () => {
        if (rating === undefined) return 'text-ink/70';
        if (rating >= 8) return 'text-green-600';
        if (rating >= 6) return 'text-amber-600';
        return 'text-red-500';
    };

    return (
        <div
            onClick={onClick}
            className={`
                inline-flex items-center ${styles.container}
                rounded-lg border-2
                transition-all duration-200
                ${onClick ? 'cursor-pointer hover:scale-[1.02] hover:shadow-md' : ''}
                ${selected 
                    ? 'bg-mint border-mint text-ink shadow-md' 
                    : 'bg-white border-ink/30 text-ink hover:border-ink/50'
                }
            `}
            title={displayName}
        >
            <img
                src={iconUrl}
                alt=""
                className={`${styles.icon} object-contain flex-shrink-0`}
                onError={(e) => {
                    (e.target as HTMLImageElement).src = getCoffeeIconUrl('espresso');
                }}
            />
            <div className="flex flex-col min-w-0">
                <span className={`${styles.name} truncate text-ink`}>
                    {displayName}
                </span>
                {(rating !== undefined || count !== undefined) && (
                    <span className={`${styles.stats} ${selected ? 'text-ink/80' : getRatingColor()}`}>
                        {rating !== undefined && rating.toFixed(1)}
                        {count !== undefined && <span className="text-ink/60 font-medium"> ({count})</span>}
                    </span>
                )}
            </div>
        </div>
    );
};

export default CoffeeBadge;
